import { useState, useCallback } from 'react';
import type { TemplateConfig } from '@/mocks/template-config';
import type { TemplatePreset } from '@/mocks/template-presets';
import { getCategories, categories } from '@/mocks/template-presets';
import ImagePicker from '@/components/ImagePicker';

interface PresetLibraryFormProps {
  presets: TemplatePreset[];
  activePresetId: string | null;
  templateConfig: TemplateConfig;
  onSetActive: (id: string | null) => void;
  onTogglePreset: (id: string) => void;
  onSavePreset: (preset: TemplatePreset) => void;
  onApplyPreset: (preset: TemplatePreset) => void;
}

export default function PresetLibraryForm({
  presets,
  activePresetId,
  onSetActive,
  onTogglePreset,
  onSavePreset,
  onApplyPreset,
}: PresetLibraryFormProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [editingPreset, setEditingPreset] = useState<TemplatePreset | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newPreset, setNewPreset] = useState<Partial<TemplatePreset>>({
    name: '',
    category: 'cameras',
    primaryColor: '#0a1628',
    secondaryColor: '#c9a96e',
    title: 'PROPOSTA COMERCIAL',
    subtitle: '',
    description: '',
    active: true,
  });

  const filtered = selectedCategory === 'all'
    ? presets
    : presets.filter((p) => p.category === selectedCategory);

  const handleCreate = useCallback(() => {
    if (!newPreset.name || !newPreset.category) return;
    const id = `preset-${Date.now()}`;
    const preset: TemplatePreset = {
      id,
      name: newPreset.name,
      category: newPreset.category as TemplatePreset['category'],
      primaryColor: newPreset.primaryColor || '#0a1628',
      secondaryColor: newPreset.secondaryColor || '#c9a96e',
      topBannerDesktop: '',
      topBannerMobile: '',
      mainImage: '',
      pdfCoverImage: '',
      pdfBackCoverImage: '',
      footerImage: '',
      title: newPreset.title || 'PROPOSTA COMERCIAL',
      subtitle: newPreset.subtitle || '',
      description: newPreset.description || '',
      activeSpecialties: [],
      active: true,
    };
    onSavePreset(preset);
    setShowCreate(false);
    setNewPreset({
      name: '',
      category: 'cameras',
      primaryColor: '#0a1628',
      secondaryColor: '#c9a96e',
      title: 'PROPOSTA COMERCIAL',
      subtitle: '',
      description: '',
      active: true,
    });
  }, [newPreset, onSavePreset]);

  const handleUpdate = useCallback(() => {
    if (!editingPreset) return;
    onSavePreset(editingPreset);
    setEditingPreset(null);
  }, [editingPreset, onSavePreset]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-semibold text-lg">Biblioteca / Presets</h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium px-4 py-2 rounded-md text-sm transition-colors cursor-pointer"
        >
          <i className="ri-add-line" />
          Novo Preset
        </button>
      </div>

      <p className="text-brand-text-muted text-sm">
        Escolha um preset para aplicar automaticamente imagens, cores, título e especialidades ao template.
      </p>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${
            selectedCategory === 'all'
              ? 'bg-brand-gold text-brand-dark font-medium'
              : 'bg-brand-dark/60 text-brand-text-muted border border-brand-gold/20 hover:text-white'
          }`}
        >
          Todas
        </button>
        {getCategories().map((cat) => (
          <button
            key={cat.slug}
            onClick={() => setSelectedCategory(cat.slug)}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${
              selectedCategory === cat.slug
                ? 'bg-brand-gold text-brand-dark font-medium'
                : 'bg-brand-dark/60 text-brand-text-muted border border-brand-gold/20 hover:text-white'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Create Preset Form */}
      {showCreate && (
        <div className="bg-brand-dark/60 border border-brand-gold/20 rounded-lg p-5 space-y-4">
          <h3 className="text-white font-medium text-sm">Criar Novo Preset</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-brand-text-muted text-xs mb-1 block">Nome</label>
              <input
                type="text"
                value={newPreset.name}
                onChange={(e) => setNewPreset({ ...newPreset, name: e.target.value })}
                className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                placeholder="Ex: Câmeras Premium"
              />
            </div>
            <div>
              <label className="text-brand-text-muted text-xs mb-1 block">Categoria</label>
              <select
                value={newPreset.category}
                onChange={(e) => setNewPreset({ ...newPreset, category: e.target.value as TemplatePreset['category'] })}
                className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
              >
                {categories.map((cat) => (
                  <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-brand-text-muted text-xs mb-1 block">Cor Principal</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newPreset.primaryColor}
                  onChange={(e) => setNewPreset({ ...newPreset, primaryColor: e.target.value })}
                  className="w-10 h-8 rounded cursor-pointer"
                />
                <span className="text-brand-text-muted text-xs">{newPreset.primaryColor}</span>
              </div>
            </div>
            <div>
              <label className="text-brand-text-muted text-xs mb-1 block">Cor Secundária</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newPreset.secondaryColor}
                  onChange={(e) => setNewPreset({ ...newPreset, secondaryColor: e.target.value })}
                  className="w-10 h-8 rounded cursor-pointer"
                />
                <span className="text-brand-text-muted text-xs">{newPreset.secondaryColor}</span>
              </div>
            </div>
            <div>
              <label className="text-brand-text-muted text-xs mb-1 block">Título Padrão</label>
              <input
                type="text"
                value={newPreset.title}
                onChange={(e) => setNewPreset({ ...newPreset, title: e.target.value })}
                className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
              />
            </div>
            <div>
              <label className="text-brand-text-muted text-xs mb-1 block">Subtítulo Padrão</label>
              <input
                type="text"
                value={newPreset.subtitle}
                onChange={(e) => setNewPreset({ ...newPreset, subtitle: e.target.value })}
                className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                placeholder="Subtítulo da proposta"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreate(false)}
              className="flex-1 border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 py-2 rounded-md text-sm transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreate}
              className="flex-1 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium py-2 rounded-md text-sm transition-colors cursor-pointer"
            >
              Criar Preset
            </button>
          </div>
        </div>
      )}

      {/* Presets List */}
      <div className="space-y-3">
        {filtered.map((preset) => {
          const isActive = activePresetId === preset.id;
          const catName = categories.find((c) => c.slug === preset.category)?.name || preset.category;
          return (
            <div
              key={preset.id}
              className={`bg-brand-dark/60 border rounded-lg p-4 transition-colors ${
                isActive ? 'border-brand-gold' : 'border-brand-gold/20'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Image Preview */}
                <div className="w-20 h-14 rounded-md overflow-hidden flex-shrink-0 bg-brand-darker">
                  {preset.topBannerDesktop ? (
                    <img
                      src={preset.topBannerDesktop}
                      alt={preset.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-brand-text-muted text-sm" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium text-sm truncate">{preset.name}</h3>
                    <span className="text-brand-text-muted text-xs bg-brand-dark/60 px-2 py-0.5 rounded-full">
                      {catName}
                    </span>
                    {isActive && (
                      <span className="text-brand-gold text-xs bg-brand-gold/10 px-2 py-0.5 rounded-full font-medium">
                        ATIVO
                      </span>
                    )}
                  </div>
                  <p className="text-brand-text-muted text-xs mb-2 line-clamp-2">
                    {preset.description || preset.subtitle || 'Sem descrição'}
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: preset.primaryColor }} />
                    <div className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: preset.secondaryColor }} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onApplyPreset(preset)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                        isActive
                          ? 'bg-brand-gold/20 text-brand-gold border border-brand-gold/30'
                          : 'bg-brand-gold hover:bg-brand-gold-light text-brand-dark'
                      }`}
                    >
                      {isActive ? 'Aplicar Novamente' : 'Aplicar ao Template'}
                    </button>
                    <button
                      onClick={() => onSetActive(isActive ? null : preset.id)}
                      className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer border ${
                        isActive
                          ? 'border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10'
                          : 'border-brand-gold/20 text-brand-text-muted hover:text-white'
                      }`}
                    >
                      {isActive ? 'Desativar' : 'Definir Ativo'}
                    </button>
                    <button
                      onClick={() => setEditingPreset(editingPreset?.id === preset.id ? null : preset)}
                      className="px-3 py-1.5 rounded-md text-xs border border-brand-gold/20 text-brand-text-muted hover:text-white transition-colors cursor-pointer"
                    >
                      {editingPreset?.id === preset.id ? 'Fechar' : 'Editar'}
                    </button>
                    <button
                      onClick={() => onTogglePreset(preset.id)}
                      className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer border ${
                        preset.active
                          ? 'border-green-500/30 text-green-400 hover:bg-green-500/10'
                          : 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                      }`}
                    >
                      {preset.active ? 'Ativo' : 'Inativo'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Edit Form */}
              {editingPreset?.id === preset.id && (
                <div className="mt-4 pt-4 border-t border-brand-gold/10 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-brand-text-muted text-xs mb-1 block">Título</label>
                      <input
                        type="text"
                        value={editingPreset.title}
                        onChange={(e) => setEditingPreset({ ...editingPreset, title: e.target.value })}
                        className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                      />
                    </div>
                    <div>
                      <label className="text-brand-text-muted text-xs mb-1 block">Subtítulo</label>
                      <input
                        type="text"
                        value={editingPreset.subtitle}
                        onChange={(e) => setEditingPreset({ ...editingPreset, subtitle: e.target.value })}
                        className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-brand-text-muted text-xs mb-1 block">Descrição</label>
                      <textarea
                        value={editingPreset.description}
                        onChange={(e) => setEditingPreset({ ...editingPreset, description: e.target.value })}
                        rows={2}
                        className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50 resize-none"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <ImagePicker
                        label="Banner Desktop"
                        value={editingPreset.topBannerDesktop}
                        onChange={(v) => setEditingPreset({ ...editingPreset, topBannerDesktop: v })}
                        type="top_banner_desktop"
                        category={editingPreset.category}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <ImagePicker
                        label="Banner Mobile"
                        value={editingPreset.topBannerMobile}
                        onChange={(v) => setEditingPreset({ ...editingPreset, topBannerMobile: v })}
                        type="top_banner_mobile"
                        category={editingPreset.category}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <ImagePicker
                        label="Imagem Principal"
                        value={editingPreset.mainImage}
                        onChange={(v) => setEditingPreset({ ...editingPreset, mainImage: v })}
                        type="main_image"
                        category={editingPreset.category}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <ImagePicker
                        label="Imagem Rodapé"
                        value={editingPreset.footerImage}
                        onChange={(v) => setEditingPreset({ ...editingPreset, footerImage: v })}
                        type="footer_banner"
                        category={editingPreset.category}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <ImagePicker
                        label="Capa PDF"
                        value={editingPreset.pdfCoverImage}
                        onChange={(v) => setEditingPreset({ ...editingPreset, pdfCoverImage: v })}
                        type="pdf_cover"
                        category={editingPreset.category}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <ImagePicker
                        label="Contracapa PDF"
                        value={editingPreset.pdfBackCoverImage}
                        onChange={(v) => setEditingPreset({ ...editingPreset, pdfBackCoverImage: v })}
                        type="pdf_back_cover"
                        category={editingPreset.category}
                      />
                    </div>
                    <div>
                      <label className="text-brand-text-muted text-xs mb-1 block">Cor Principal</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editingPreset.primaryColor}
                          onChange={(e) => setEditingPreset({ ...editingPreset, primaryColor: e.target.value })}
                          className="w-10 h-8 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={editingPreset.primaryColor}
                          onChange={(e) => setEditingPreset({ ...editingPreset, primaryColor: e.target.value })}
                          className="flex-1 bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-brand-text-muted text-xs mb-1 block">Cor Secundária</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editingPreset.secondaryColor}
                          onChange={(e) => setEditingPreset({ ...editingPreset, secondaryColor: e.target.value })}
                          className="w-10 h-8 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={editingPreset.secondaryColor}
                          onChange={(e) => setEditingPreset({ ...editingPreset, secondaryColor: e.target.value })}
                          className="flex-1 bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setEditingPreset(null)}
                      className="flex-1 border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 py-2 rounded-md text-sm transition-colors cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleUpdate}
                      className="flex-1 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium py-2 rounded-md text-sm transition-colors cursor-pointer"
                    >
                      Salvar Alterações
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full border border-brand-gold/20 flex items-center justify-center mx-auto mb-3">
              <i className="ri-folder-image-line text-brand-text-muted text-lg" />
            </div>
            <p className="text-brand-text-muted text-sm">Nenhum preset encontrado nesta categoria.</p>
          </div>
        )}
      </div>
    </div>
  );
}